import CloseButton from 'src/components/Buttons/CloseButton';
import config from 'src/constants/config';
import DateFormat from 'src/utils/dateFormat';
import Modal from '../../../utils/modal';
import moment from 'src/utils/moment';
import NormalButton from '../../Buttons/NormalButton';
import NotFound from 'src/components/Layouts/NotFound';
import React, { useCallback, useRef } from 'react';
import StorageService from 'src/services/storage.service';
import SVG from 'src/components/Images/SvgRenderer';
import TabsMenu from 'src/components/Menus/TabsMenu';
import theme from 'src/ui/theme';
import TooltipIcon from 'src/components/Icons/TooltipIcon';
import useStyles from './style';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import { createNotification } from 'src/utils/createNotification';
import { getUserSetting, saveUserSettings, updateUserSetting } from 'src/utils/useUser';
import { isCypress } from '../../../utils/useCypress';
import { setConfirmModal, setDraftsModal } from '../../../store/actions/modals.actions';
import { setUserSettings } from 'src/store/actions/user.actions';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux-hooks';
import { useStates } from 'src/utils/useState';
import { useTranslation } from 'react-i18next';
import { yesOrNo } from 'src/utils/useFunctions';

const DraftsModal: React.FunctionComponent = () => {

  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const configurationData = useAppSelector((state: any) => state.configuration);
  const dataData = useAppSelector((state: any) => state.data);
  const languageData = useAppSelector((state: any) => state.language);
  const modalsData = useAppSelector((state: any) => state.modals);
  const userData = useAppSelector((state: any) => state.user);

  const types = config.DRAFTS_TYPES;

  const defaultType = modalsData.draftsModal.type;
  const type = defaultType === "all" ? types[0] : defaultType;
  const classbookType = modalsData.draftsModal.classbookType;
  const isSchoolCare = modalsData.draftsModal.isSchoolCare;
  const classID = modalsData.draftsModal.classID;
  const postType = modalsData.draftsModal.postType;
  const canSynchronize = modalsData.draftsModal.canSynchronize;
  const onDraftLoad = modalsData.draftsModal.onDraftLoad;
  const activityTypes = configurationData.configuration.activityTypes;

  const [state, setState] = useStates({
    isSaving: false,
    isSynchronizating: false,
    type: type,
  });

  const isSaving = useRef(state.isSaving);

  const getDraftLocalStorageTypeName = (type: any) => {
    const names: any = {
      classbook: config.LOCALSTORAGE_CLASSBOOK_DRAFTS,
      timeline: config.LOCALSTORAGE_TIMELINE_DRAFTS,
    };
    return names[type];
  };

  const getDrafts = (type: any) => {
    const isLocalStorageExist = StorageService.existItem(getDraftLocalStorageTypeName(type));
    if(isLocalStorageExist) {
      const localStorageDrafts = StorageService.getItem(getDraftLocalStorageTypeName(type)).map((item: any) => { return {...item, storage: "localStorage"}; });
      const userDataStorageDrafts = getUserSetting(userData.userSettings, "drafts", [type]).map((item: any) => { return {...item, storage: "userDataStorage"}; });
      return [...localStorageDrafts, ...userDataStorageDrafts];
    } else {
      const localStorageDrafts: any = [];
      const userDataStorageDrafts = getUserSetting(userData.userSettings, "drafts", [type]).map((item: any) => { return {...item, storage: "userDataStorage"}; });
      StorageService.setItem(getDraftLocalStorageTypeName(type), localStorageDrafts);
      return [...localStorageDrafts, ...userDataStorageDrafts];
    }
  };

  const getAvailableDrafts = (type: any) => {
    if(type === "classbook" && classbookType !== null && isSchoolCare !== null && classID !== null) {
      return getDrafts(type).filter((item: any) => item.classbookType === classbookType && item.isSchoolCare === isSchoolCare && item.classID === classID);
    } else if(type === "timeline" && postType !== null) {
      return getDrafts(type).filter((item: any) => item.postType === postType || (postType === 1 && !item.postType));
    } else {
      return getDrafts(type);
    }
  };

  const getLocalDrafts = (type: any) => {
    return getAvailableDrafts(type).filter((item: any) => item.storage === "localStorage");
  };

  const getCloudDrafts = (type: any) => {
    return getAvailableDrafts(type).filter((item: any) => item.storage === "userDataStorage");
  };

  const getActivityTypeData = (activityTypeID: any) => {
    return activityTypes.filter((activity: any) => activity.activityTypeID === activityTypeID).length === 0 ? null : activityTypes.find((activity: any) => activity.activityTypeID === activityTypeID);
  };

  const getClassData = (classID: any) => {
    return dataData.classes.filter((item: any) => item.classID === classID).length === 0 ? [] : dataData.classes.find((item: any) => item.classID === classID);
  };

  const getChildData = (childID: any) => {
    return dataData.children.filter((item: any) => item.childID === childID).length === 0 ? [] : dataData.children.find((item: any) => item.childID === childID);
  };

  const getPostType = (postTypeID: any) => {
    if(postTypeID === 3) return t('post_video');
    else if(postTypeID === 2) return t('poll');
    else return t('post');
  };

  const tabsItems: any = types.map((item: any) => {
    return {
      name: item,
      value: item,
      isEnabled: true,
      badge: getAvailableDrafts(item).filter((value: any, index: any, self: any) => index === self.findIndex((obj: any) => obj.draftID === value.draftID)).length,
      badgeType: getAvailableDrafts(item).filter((value: any, index: any, self: any) => index === self.findIndex((obj: any) => obj.draftID === value.draftID)).filter((item) => item.storage === "localStorage").length === 0 ? 'primary' : 'warning',
    };
  });

  const availableDrafts = getAvailableDrafts(state.type).filter((value: any, index: any, self: any) => index === self.findIndex((obj: any) => obj.draftID === value.draftID));
  availableDrafts.sort((a: any, b: any) => { return b.draftID - a.draftID });
  
  const onCloseModal = () => {
    const settings = {
      isOpen: false,
      type: null,
      classbookType: null,
      isSchoolCare: null,
      classID: null,
      postType: null,
      canSynchronize: null,
      onDraftLoad: null,
    };
    dispatch(setDraftsModal(settings));
  };

  const handleClose = (e: any) => {
    e.stopPropagation();
    onCloseModal();
  };

  const setType = (value: any) => {
    setState("type", value);
  };

  const handleSynchronize = async () => {
    setState("isSynchronizating", true);
    const drafts = getDrafts(state.type);
    const excludeKeys = ["storage"];
    const newDrafts = drafts.map((item: any) => {
      return Object.fromEntries(Object.entries(item).filter(([key]) => !excludeKeys.includes(key)));
    });
    const result = await handleUpdateDrafts(newDrafts);
    if(result) {
      StorageService.setItem(getDraftLocalStorageTypeName(state.type), "[]");
      createNotification(t("drafts_synchronized"), "success");
      setState("isSynchronizating", false);
    } else {
      createNotification(t("drafts_failed_synchronize"), "error");
      setState("isSynchronizating", false);
    }
  };

  const handleUpdateDrafts = useCallback(async (value: any) => {
    if(isSaving.current === false) {
      setState("isSaving", true);
      isSaving.current = true;
      const oldValue = getUserSetting(userData.userSettings, "drafts", [state.type]);
      const newValue = updateUserSetting(userData.userSettings, "drafts", [state.type], value);
      dispatch(setUserSettings(newValue));
      const result = await saveUserSettings(dispatch, userData, "drafts", [state.type], value);
      if(result) {
        isSaving.current = false;
        setState("isSaving", false);
        return true;
      } else {
        const updateSettings = updateUserSetting(userData.userSettings, "drafts", [state.type], oldValue);
        dispatch(setUserSettings(updateSettings));
        isSaving.current = false;
        setState("isSaving", false);
        return false;
      }
    }
  }, [dispatch, t, userData, state.type]);

  const handleLoadDraft = (e: any, data: any) => {
    e.stopPropagation();
    e.preventDefault();
    onDraftLoad(data);
    onCloseModal();
  };

  const handleDeleteDraft = (e: any, draftID: any, storageType: any) => {
    e.stopPropagation();
    e.preventDefault();
    const deleteDraft = async () => {
      if(storageType === "localStorage") {
        const drafts = getLocalDrafts(state.type);
        const excludeKeys = ["storage"];
        const newDrafts = drafts.map((item: any) => {
         return Object.fromEntries(Object.entries(item).filter(([key]) => !excludeKeys.includes(key)));
        });
        const newDraftData = newDrafts.filter((item: any) => item.draftID !== draftID);
        StorageService.setItem(getDraftLocalStorageTypeName(state.type), newDraftData);
      } else {
        const drafts = getCloudDrafts(state.type);
        const excludeKeys = ["storage"];
        const newDrafts = drafts.map((item: any) => {
          return Object.fromEntries(Object.entries(item).filter(([key]) => !excludeKeys.includes(key)));
        });
        const newDraftData = newDrafts.filter((item: any) => item.draftID !== draftID);
        const result = await handleUpdateDrafts(newDraftData);
        if(!result) {
          createNotification(t("draft_not_deleted"), "error");
        }
      }
    };
    const settings = {
      isOpen: true,
      title: t('draft_delete'),
      content: t('draft_delete_confirm'),
      onAccept: deleteDraft,
      onDecline: null,
    };
    dispatch(setConfirmModal(settings));
  };

  return (
    <Modal 
      open={true}
      onClose={onCloseModal}
    >
      <div className={classes.root} data-cy={isCypress() ? "conceptModal" : null}>
        <div className={classes.header}>
          <div className={classes.wrapper}>
            <p data-cy={isCypress() ? 'conceptModalTitle' : null}>
              {canSynchronize ? t('drafts_manage') : t('drafts')}
              {
                defaultType !== "all" ? (
                  <small>({availableDrafts.length})</small>
                ) : null
              }
            </p>
          </div>
          <CloseButton onClick={handleClose} dataCy="timesButton"/> 
        </div>
        <div className={classes.body}>
          {
            defaultType === "all" ? (
              <div className={classes.tabsWrapper}>
                <TabsMenu items={tabsItems} selected={state.type} onSelect={setType}/>
              </div>
            ) : null
          }
          {
            availableDrafts.length > 0 ? (
              <div className={classes.accordions}>
                {
                  availableDrafts.map((item: any, key: any) => (
                    <Accordion className={classes.accordion} data-cy={isCypress() ? 'draftAccordion' + item.draftID : null} key={`k_${key}`}>
                      <AccordionSummary className={classes.accordionSummary} expandIcon={<SVG src="chevron-down"/>}>
                        {t('draft_template_info', {draftID: (availableDrafts.length - key), date: DateFormat(moment(item.draftDate), "defaulttime", languageData, t)})}
                        <div className={classes.buttons}>
                          <TooltipIcon icon={item.storage === "localStorage" ? "storage-local-outlined" : "storage-cloud-outlined"} title={item.storage === "localStorage" ? t("draft_storage_local") : t("draft_storage_cloud")} position='top' maxWidth={400} iconColor={item.storage === "localStorage" ? theme.colors.systemOrange[500] : theme.colors.systemGreen[500]} backgroundColor={item.storage === "localStorage" ? theme.colors.systemOrange[100] : theme.colors.systemGreen[100]}/>
                          {
                            onDraftLoad ? (
                              <NormalButton buttonType='primary' className={classes.smallButton} tooltip={t("use_draft")} tooltipPosition='top' tooltipMaxWidth={400} onClick={(e: any) => handleLoadDraft(e, item)} dataCy={'draftAccordionLoad' + item.draftID} disabled={state.isSaving}>
                                {t("use")}
                              </NormalButton>
                            ) : (
                              <NormalButton buttonType='clear' className={classes.smallButton} tooltip={t("delete_draft")} tooltipPosition='top' tooltipMaxWidth={400} onClick={(e: any) => handleDeleteDraft(e, item.draftID, item.storage)} dataCy={'draftAccordionDelete' + item.draftID} disabled={state.isSaving}>
                                {t("delete")}
                              </NormalButton>
                            )
                          }
                        </div>
                      </AccordionSummary>
                      <AccordionDetails className={classes.accordionDetails}>
                        {
                          state.type === "classbook" ? (
                            <>
                              {
                                (!classID && item.classID && getClassData(item.classID).length !== 0) ? (
                                  <div className={classes.formWrapper}>
                                    <span>
                                      {t('class')}
                                    </span>
                                    <p data-cy={isCypress() ? 'classValue' : null}>{getClassData(item.classID).name}</p>
                                  </div>
                                ) : null
                              }
                              {
                                (item.theme && item.theme !== "") ? (
                                  <div className={classes.formWrapper}>
                                    <span>
                                      {t('theme')}
                                    </span>
                                    <p data-cy={isCypress() ? 'themeValue' : null}>{item.theme}</p>
                                  </div>
                                ) : null
                              }
                              {
                                (item.morning && item.morning !== "") ? (
                                  <div className={classes.formWrapper}>
                                    <span>
                                      {t('morning_educational_activities')}
                                    </span>
                                    <p data-cy={isCypress() ? 'morningValue' : null}>{item.morning}</p>
                                  </div>
                                ) : null
                              }
                              {
                                (item.afternoon && item.afternoon !== "") ? (
                                  <div className={classes.formWrapper}>
                                    <span>
                                      {t('afternoon_educational_activities')}
                                    </span>
                                    <p data-cy={isCypress() ? 'afternoonValue' : null}>{item.afternoon}</p>
                                  </div>
                                ) : null
                              }
                              {
                                (item.notes && item.notes !== "") ? (
                                  <div className={classes.formWrapper}>
                                    <span>
                                      {t('additional_notes')}
                                    </span>
                                    <p data-cy={isCypress() ? 'notesValue' : null}>{item.notes}</p>
                                  </div>
                                ) : null
                              }
                              {
                                (item.visit && item.visit !== "") ? (
                                  <div className={classes.formWrapper}>
                                    <span>
                                      {t('record_visits')}
                                    </span>
                                    <p data-cy={isCypress() ? 'visitValue' : null}>{item.visit}</p>
                                  </div>
                                ) : null
                              }
                              {
                                (item.interestActivities && item.interestActivities !== "") ? (
                                  <div className={classes.formWrapper}>
                                    <span>
                                      {t('interest_activities')}
                                    </span>
                                    <p data-cy={isCypress() ? 'interestActivitiesValue' : null}>{item.interestActivities}</p>
                                  </div>
                                ) : null
                              }
                              {
                                (item.relaxationRecreationalActivities && item.relaxationRecreationalActivities !== "") ? (
                                  <div className={classes.formWrapper}>
                                    <span>
                                      {t('relaxation_recreational_activities')}
                                    </span>
                                    <p data-cy={isCypress() ? 'relaxationRecreationalActivitiesValue' : null}>{item.relaxationRecreationalActivities}</p>
                                  </div>
                                ) : null
                              }
                              {
                                (item.preparationTeaching && item.preparationTeaching !== "") ? (
                                  <div className={classes.formWrapper}>
                                    <span>
                                      {t('preparation_teaching')}
                                    </span>
                                    <p data-cy={isCypress() ? 'preparationTeachingValue' : null}>{item.preparationTeaching}</p>
                                  </div>
                                ) : null
                              }
                              {
                                (item.otherActivities && item.otherActivities !== "") ? (
                                  <div className={classes.formWrapper}>
                                    <span>
                                      {t('other_activities')}
                                    </span>
                                    <p data-cy={isCypress() ? 'otherActivitiesValue' : null}>{item.otherActivities}</p>
                                  </div>
                                ) : null
                              }
                              {
                                (item.internalNote && item.internalNote !== "") ? (
                                  <div className={classes.formWrapper}>
                                    <span>
                                      {t('internal_note')}
                                    </span>
                                    <p data-cy={isCypress() ? 'internalNoteValue' : null}>{item.internalNote}</p>
                                  </div>
                                ) : null
                              }
                            </>
                          ) : state.type === "timeline" ? (
                            <>
                              <div className={classes.formWrapper}>
                                <span>
                                  {t('post_type')}
                                </span>
                                <p data-cy={isCypress() ? 'postTypeValue' : null}>{getPostType(postType ? postType : item.postType)}</p>
                              </div>
                              {
                                (item.title && item.title !== "") ? (
                                  <div className={classes.formWrapper}>
                                    <span>
                                      {t('title')}
                                    </span>
                                    <p data-cy={isCypress() ? 'titleValue' : null}>{item.title}</p>
                                  </div>
                                ) : null
                              }
                              {
                                (item.description && item.description !== "") ? (
                                  <div className={classes.formWrapper}>
                                    <span>
                                      {t('post_content')}
                                    </span>
                                    <p data-cy={isCypress() ? 'descriptionValue' : null}>{item.description}</p>
                                  </div>
                                ) : null
                              }
                              {
                                (item.answers && Array.isArray(item.answers) && item.answers.filter((item: any) => item.answer !== "").length !== 0) ? (
                                  <div className={classes.formWrapper}>
                                    <span>
                                      {t('answers')}
                                    </span>
                                    {
                                      item.multipleAnswers ? (
                                        <p data-cy={isCypress() ? 'multipleAnswersValue' : null}>{t('multiple_answers')}</p>
                                      ) : null
                                    }
                                    <p data-cy={isCypress() ? 'answersValue' : null}>{item.answers.filter((item: any) => item.answer !== "").map((subItem: any) => { return `${subItem.answer}${(item.limitAnswers) ? ` (${subItem.limit.toString()})` : ''}`; }).join(", ")}</p>
                                  </div>
                                ) : null
                              }
                              {
                                (item.timelimitPoll && item.pollLimit) ? (
                                  <div className={classes.formWrapper}>
                                    <span>
                                      {t('answers_limit')}
                                    </span>
                                    <p data-cy={isCypress() ? 'pollLimitValue' : null}>{DateFormat(moment(item.pollLimit).toString(),"defaulttime", languageData, t)}</p>
                                  </div>
                                ) : null
                              }
                              {
                                (item.activityType && item.activityType !== null) ? (
                                  <div className={classes.formWrapper}>
                                    <span>
                                      {t('activity_type')}
                                    </span>
                                    <p data-cy={isCypress() ? 'activityTypeValue' : null}>{getActivityTypeData(item.activityType).name}</p>
                                  </div>
                                ) : null
                              }
                              {
                                (item.selectedChildren && item.selectedChildren.length !== 0) ? (
                                  <div className={classes.formWrapper}>
                                    <span>
                                      {t('children')}
                                    </span>
                                    <p data-cy={isCypress() ? 'selectedChildrenValue' : null}>{item.selectedChildren.map((item: any) => { return getChildData(item.childID).displayName; }).join(", ")}</p>
                                  </div>
                                ) : null
                              }
                              {
                                (item.modules && Object.keys(item.modules).length !== 0) ? (
                                  <>
                                    {
                                      item.modules.reactions !== undefined && item.modules.reactions !== null ? (
                                        <div className={classes.formWrapper}>
                                          <span>
                                            {t('reactions')}
                                          </span>
                                          <p data-cy={isCypress() ? 'reactionsValue' : null}>{t(yesOrNo(item.modules.reactions))}</p>
                                        </div>
                                      ) : null
                                    }
                                    {
                                      item.modules.comments !== undefined && item.modules.comments !== null ? (
                                        <div className={classes.formWrapper}>
                                          <span>
                                            {t('comments')}
                                          </span>
                                          <p data-cy={isCypress() ? 'commentsValue' : null}>{t(yesOrNo(item.modules.comments))}</p>
                                        </div>
                                      ) : null
                                    }
                                  </>
                                ) : null
                              }
                            </>
                          ) : null
                        }
                      </AccordionDetails>
                    </Accordion>
                  ))
                }
              </div>
            ) : (
              <NotFound text={t('no_drafts_found')}/>
            )
          }
        </div>
        {
          canSynchronize ? (
            <div className={classes.footer}>
              <NormalButton buttonType="primary" onClick={handleSynchronize} dataCy="draftsModalSynchronize" disabled={state.isSaving || getLocalDrafts(state.type).length === 0}>
                {state.isSynchronizating ? t("draft_storage_synchronizing") : t("draft_storage_synchronize")}
              </NormalButton>
            </div>
          ) : null
        }
      </div>
    </Modal>
  );
};

export default DraftsModal;